import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppStore } from '../services/store';

type TabType = 'installed' | 'trials' | 'requests';

export default function MyApps() {
  const { apps, requests, loadInitialData, uninstallApp } = useAppStore();
  const [activeTab, setActiveTab] = useState<TabType>('installed');

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const installedApps = apps.filter(app => app.status === 'Installed');
  const trialApps = apps.filter(app => app.status === 'Trial');
  const pendingRequests = requests.filter(req => req.status === 'Pending');

  const handleUninstall = (appId: string) => {
    if (confirm('Are you sure you want to uninstall this app?')) {
      uninstallApp(appId);
    }
  };

  const renderAppCard = (app: any, isTrial: boolean = false) => (
    <div key={app.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
          <img src={app.coverImage} alt={app.name} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{app.name}</h3>
              <p className="text-sm text-gray-600">{app.category}</p>
            </div>
            {isTrial && (
              <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                Trial - 30 days
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{app.description}</p>
          <div className="flex items-center gap-3">
            <Link
              to={`/app/appstore/apps/${app.id}`}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm whitespace-nowrap"
            >
              <i className="ri-external-link-line mr-1"></i>
              Launch
            </Link>
            <Link
              to={`/app/appstore/apps/${app.id}`}
              className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm whitespace-nowrap"
            >
              View Details
            </Link>
            <button
              onClick={() => handleUninstall(app.id)}
              className="border border-red-200 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors text-sm whitespace-nowrap ml-auto"
            >
              <i className="ri-delete-bin-line mr-1"></i>
              Uninstall
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRequestCard = (request: any) => {
    const statusColors = {
      'Pending': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'Approved': 'bg-green-100 text-green-700 border-green-200',
      'Rejected': 'bg-red-100 text-red-700 border-red-200'
    };

    return (
      <div key={request.id} className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{request.appName}</h3>
            <p className="text-sm text-gray-600">{request.category}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[request.status]}`}>
            {request.status}
          </span>
        </div>
        <p className="text-sm text-gray-700 mb-3">{request.description}</p>
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <span className="text-gray-500">Requested by:</span>
            <p className="font-medium text-gray-900">{request.requesterName}</p>
          </div>
          <div>
            <span className="text-gray-500">Priority:</span>
            <p className="font-medium text-gray-900">{request.priority}</p>
          </div>
          <div>
            <span className="text-gray-500">Expected Users:</span>
            <p className="font-medium text-gray-900">{request.expectedUsers}</p>
          </div>
          <div>
            <span className="text-gray-500">Request Date:</span>
            <p className="font-medium text-gray-900">{new Date(request.requestDate).toLocaleDateString()}</p>
          </div>
        </div>
        {request.adminNotes && (
          <div className="bg-gray-50 rounded-lg p-3 mt-3">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">Admin Notes:</span> {request.adminNotes}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">My Apps</h1>
          <p className="text-lg text-gray-600">Manage your installed applications and requests</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('installed')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'installed'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-apps-line mr-2"></i>
              Installed Apps ({installedApps.length})
            </button>
            <button
              onClick={() => setActiveTab('trials')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'trials'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-timer-line mr-2"></i>
              Trial Apps ({trialApps.length})
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'requests'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-file-list-line mr-2"></i>
              My Requests ({pendingRequests.length})
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {activeTab === 'installed' && (
            <>
              {installedApps.length > 0 ? (
                installedApps.map(app => renderAppCard(app))
              ) : (
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-apps-line text-3xl text-gray-400"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">No installed apps yet</h3>
                  <p className="text-gray-600 mb-6">Browse the app store to find and install applications</p>
                  <Link
                    to="/app/appstore/apps"
                    className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <i className="ri-store-line mr-2"></i>
                    Browse App Store
                  </Link>
                </div>
              )}
            </>
          )}

          {activeTab === 'trials' && (
            <>
              {trialApps.length > 0 ? (
                trialApps.map(app => renderAppCard(app, true))
              ) : (
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-timer-line text-3xl text-gray-400"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">No trial apps active</h3>
                  <p className="text-gray-600 mb-6">Start a free trial to test apps before purchasing</p>
                  <Link
                    to="/app/appstore/apps"
                    className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <i className="ri-store-line mr-2"></i>
                    Browse App Store
                  </Link>
                </div>
              )}
            </>
          )}

          {activeTab === 'requests' && (
            <>
              {pendingRequests.length > 0 ? (
                pendingRequests.map(request => renderRequestCard(request))
              ) : (
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-file-list-line text-3xl text-gray-400"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">No pending requests</h3>
                  <p className="text-gray-600 mb-6">Request new apps or features from the app store</p>
                  <Link
                    to="/app/appstore/apps"
                    className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <i className="ri-store-line mr-2"></i>
                    Browse App Store
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
